import React, { useRef, useEffect, useState } from 'react';
import './CameraCapture.css';

export default function CameraCapture({ onCapture, onClose, maxPhotos = 5, currentCount = 0 }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const initialCount = useRef(currentCount); // einfrieren beim Öffnen — nicht nachlaufend
  const closingRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [capturedThumbs, setCapturedThumbs] = useState([]);
  const [flash, setFlash] = useState(false);
  const remaining = maxPhotos - initialCount.current - capturedThumbs.length;

  useEffect(() => {
    // Body-Scroll sperren solange Kamera offen
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    // Dummy-History-Eintrag damit der Zurück-Button die Kamera schließt statt zu navigieren
    window.history.pushState({ cameraOpen: true }, '');

    function onPopState() {
      if (closingRef.current) return;
      closingRef.current = true;
      stopStream();
      onClose();
    }

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          setReady(true);
        }
      } catch {
        setError('Kamera konnte nicht gestartet werden.\nBitte erlaube den Kamerazugriff in den Browser-Einstellungen.');
      }
    }
    startCamera();
    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  function stopStream() {
    streamRef.current?.getTracks().forEach(t => t.stop());
  }

  function handleDone() {
    if (closingRef.current) return;
    closingRef.current = true;
    stopStream();
    // Dummy-Eintrag aus History entfernen
    if (window.history.state?.cameraOpen) {
      window.history.back();
    }
    onClose();
  }

  function capture() {
    if (remaining <= 0) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);

    // Flash-Effekt
    setFlash(true);
    setTimeout(() => setFlash(false), 150);

    canvas.toBlob(blob => {
      const idx = currentCount + capturedThumbs.length + 1;
      const file = new File([blob], `kamera-foto-${idx}.jpg`, { type: 'image/jpeg' });
      const thumb = URL.createObjectURL(blob);
      setCapturedThumbs(prev => [...prev, thumb]);
      onCapture(file);
    }, 'image/jpeg', 0.92);
  }

  return (
    <div className="camera-overlay">
      {error ? (
        <div className="camera-error">
          <span className="camera-error-icon">📷</span>
          <p>{error}</p>
          <button className="camera-error-btn" onClick={handleDone}>Schließen</button>
        </div>
      ) : (
        <>
          {flash && <div className="camera-flash" />}

          <button className="camera-close" onClick={handleDone} aria-label="Schließen">✕</button>

          {remaining > 0 && (
            <div className="camera-remaining">noch {remaining} Foto{remaining !== 1 ? 's' : ''} möglich</div>
          )}

          <video ref={videoRef} className="camera-video" playsInline muted autoPlay />

          {capturedThumbs.length > 0 && (
            <div className="camera-thumb-strip">
              {capturedThumbs.map((src, i) => (
                <img key={i} src={src} alt={`Foto ${i + 1}`} className="camera-thumb" />
              ))}
            </div>
          )}

          {capturedThumbs.length > 0 && (
            <button className="camera-done-btn" onClick={handleDone}>
              ✓ Fertig ({capturedThumbs.length})
            </button>
          )}

          {ready && (
            <div className="camera-controls">
              <button
                className="camera-shutter"
                onClick={capture}
                disabled={remaining <= 0}
                aria-label="Foto aufnehmen"
              >
                <span className="camera-shutter-inner" />
              </button>
            </div>
          )}

          {!ready && !error && (
            <div className="camera-loading">Kamera wird gestartet …</div>
          )}
        </>
      )}
    </div>
  );
}
